import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/realtime-js';

const REALTIME_TABLES = [
  'project',
  'link_persona_situation',
  'link_persona_situation_pain',
  'link_solution_pain',
  'persona',
  'situation',
  'pain',
  'solution',
  'prd',
  'deliverable',
] as const;

const DEBOUNCE_MS = 300;
const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 30000;
const MAX_RECONNECTS = 5;

export function useRealtimePSPS(
  projectId: number | null,
  onInvalidate: () => void
) {
  const callbackRef = useRef(onInvalidate);
  callbackRef.current = onInvalidate;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedInvalidate = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      callbackRef.current();
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Refetch on tab focus — catches stale data after WebSocket disconnect
  useEffect(() => {
    if (projectId == null) return;

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        callbackRef.current();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [projectId]);

  // Realtime subscription with auto-reconnect on channel error
  useEffect(() => {
    if (projectId == null) return;

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let removed = false;
    let removing = false;
    let attempts = 0;

    function subscribe() {
      const channel = supabase.channel(`psps-changes-${projectId}-${Date.now()}`);

      for (const table of REALTIME_TABLES) {
        channel.on(
          'postgres_changes',
          { event: '*', schema: 'public', table },
          () => { debouncedInvalidate(); }
        );
      }

      channel.subscribe((status) => {
        if (removed || removing) return;
        if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
          attempts = 0;
          return;
        }
        if (status === REALTIME_SUBSCRIBE_STATES.CLOSED || status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR || status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT) {
          removing = true;
          supabase.removeChannel(channel);
          removing = false;
          attempts++;
          if (attempts > MAX_RECONNECTS) return;
          const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempts - 1), RECONNECT_MAX_MS);
          reconnectTimer = setTimeout(() => {
            if (!removed) {
              callbackRef.current();
              subscribe();
            }
          }, delay);
        }
      });

      return channel;
    }

    const channel = subscribe();

    return () => {
      removed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      supabase.removeChannel(channel);
    };
  }, [projectId, debouncedInvalidate]);
}
